// src/utils/toneMigration.js
// ─── Tone migration helpers ──────────────────────────────────────────────
// Centralizes FE state ↔ backend `tone_hint` conversion + legacy fallback.
//
// Backend semantics:
//   - tone_hint: "brand-voice"  → use the tenant brand voice verbatim
//   - tone_hint: null / omitted → same as "brand-voice" (no stylistic override)
//   - any other string          → free-form override (unknown slugs pass through)
//
// Legacy shape (pre-refactor entries still in-memory this session):
//   tone_overrides: { style: X }  or  tone_overrides: { custom: X }
//
// Preset registry: the live dropdown is served per-tenant by
// GET /api/v1/context/tone-presets, so the set of "known" slugs is not static.
// useContextOptions calls registerTonePresets() once the list lands; the
// inverters below consult the registry UNION the hardcoded fallback, so a
// tenant-specific slug is recognised as a preset instead of being misread as
// free text (which would strand the user in "Personalizzato" with a raw slug).
// A module-level registry avoids threading the list through six call sites, two
// of which (paramsToFormState, briefEnvelopeToFormState) are pure utilities.

import { TONE_OPTIONS } from '../constants/options.js';

let registeredPresets = [];

/** Called by useContextOptions when the BE preset list resolves. */
export function registerTonePresets(options) {
  registeredPresets = Array.isArray(options) ? options : [];
}

/** Test/teardown helper — restores the hardcoded-only behaviour. */
export function resetTonePresets() {
  registeredPresets = [];
}

function knownOptions() {
  // Registry first so a tenant label overrides the stale hardcoded one; the
  // fallback keeps historical slugs resolvable after a preset is removed.
  return [...registeredPresets, ...TONE_OPTIONS].filter(o => o.value !== 'custom');
}

function knownValues() {
  return new Set(knownOptions().map(o => o.value));
}

// Label → slug map (case-insensitive). Lets us recognize examples whose
// tone_hint was authored as the human label ("Bold & Provocative") instead
// of the canonical slug ("bold-provocative").
function labelToValue() {
  const map = new Map();
  // Reverse order so registry entries win on a label collision.
  for (const o of [...knownOptions()].reverse()) {
    if (o.label) map.set(String(o.label).toLowerCase(), o.value);
  }
  return map;
}

export function resolveToneHint(state) {
  if (!state) return null;
  if (state.tone === 'custom') {
    const trimmed = (state.customTone || '').trim();
    return trimmed || null;
  }
  return state.tone || null;
}

export function toneStateFromParams(params) {
  if (!params) return {};
  const hint =
    params.tone_hint ??
    params.tone_overrides?.style ??
    params.tone_overrides?.custom ??
    null;
  if (!hint) return {};
  if (knownValues().has(hint)) return { tone: hint, customTone: '' };
  if (typeof hint === 'string') {
    const slugFromLabel = labelToValue().get(hint.toLowerCase());
    if (slugFromLabel) return { tone: slugFromLabel, customTone: '' };
  }
  return { tone: 'custom', customTone: hint };
}

export function displayToneFromParams(params) {
  if (!params) return null;
  return (
    params.tone_hint ??
    params.tone_overrides?.style ??
    params.tone_overrides?.custom ??
    null
  );
}
